import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col, Form } from "react-bootstrap";

const initialFormState = {
  remitter_bank_name: "",
  account_holder_name: "",
  contact_no: "",
  amount: "",
  amount_in_words: "",
  ifsc_code: "",
  utr_reference_no: "",
  reference_no: "",
  bank_details: "",
};

const StdRTGS = () => {
  const navigate = useNavigate();
  const [transactionDate, setTransactionDate] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [applications, setApplications] = useState([]);
  const [defaultBankDetails, setDefaultBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const studentId = sessionStorage.getItem("userId");
  const academicYearId = localStorage.getItem("academicSessionId") || "";
  const orgId = localStorage.getItem("orgId") || "";
  const branchId = localStorage.getItem("branchId") || "";

  const resetForm = (bankDetailsText = "") => {
    setFormData({
      ...initialFormState,
      bank_details: bankDetailsText,
    });
    setTransactionDate(null);
  };

  const fetchApplications = async () => {
    if (!studentId) {
      setError("Student session details are missing. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const queryParams = new URLSearchParams();
      if (academicYearId) queryParams.append("academic_year_id", academicYearId);
      if (orgId) queryParams.append("org_id", orgId);
      if (branchId) queryParams.append("branch_id", branchId);

      const response = await fetch(
        `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentRTGSApplications/${studentId}/?${queryParams.toString()}`
      );

      const contentType = response.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok || !payload || payload.message !== "Success") {
        throw new Error(payload?.message || "Unable to load RTGS applications.");
      }

      const rows = Array.isArray(payload.data) ? payload.data : [];
      const bankDetails = payload.default_bank_details || null;

      setApplications(rows);
      setDefaultBankDetails(bankDetails);

      setFormData((prev) => ({
        ...prev,
        bank_details:
          prev.bank_details || bankDetails?.display_text || "",
      }));
    } catch (fetchError) {
      console.error("Error fetching RTGS applications:", fetchError);
      setApplications([]);
      setDefaultBankDetails(null);
      setError(fetchError.message || "Unable to load RTGS applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "ifsc_code" ? value.toUpperCase() : value,
    }));
  };

  const validateForm = () => {
    if (!studentId || !academicYearId || !orgId || !branchId) {
      return "Student session details are incomplete. Please log in again.";
    }

    if (
      !formData.remitter_bank_name.trim() ||
      !formData.account_holder_name.trim() ||
      !formData.contact_no.trim() ||
      !transactionDate ||
      !formData.amount.trim() ||
      !formData.amount_in_words.trim() ||
      !formData.ifsc_code.trim() ||
      !formData.utr_reference_no.trim()
    ) {
      return "Please fill all required fields.";
    }

    if (!/^\d{10,15}$/.test(formData.contact_no.trim())) {
      return "Contact number must be between 10 and 15 digits.";
    }

    if (Number(formData.amount) <= 0 || Number.isNaN(Number(formData.amount))) {
      return "Amount should be greater than zero.";
    }

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code.trim())) {
      return "Please enter a valid IFSC code.";
    }

    return "";
  };

  const formatDate = (date) => {
    if (!date) return "";
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split("T")[0];
  };

  const handleSave = async () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      setSuccessMessage("");
      setError(validationMessage);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const response = await fetch(
        `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentRTGSApplications/${studentId}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            student_id: studentId,
            academic_year_id: academicYearId,
            org_id: orgId,
            branch_id: branchId,
            remitter_bank_name: formData.remitter_bank_name.trim(),
            account_holder_name: formData.account_holder_name.trim(),
            contact_no: formData.contact_no.trim(),
            transaction_date: formatDate(transactionDate),
            amount: formData.amount.trim(),
            amount_in_words: formData.amount_in_words.trim(),
            ifsc_code: formData.ifsc_code.trim(),
            utr_reference_no: formData.utr_reference_no.trim(),
            reference_no: formData.reference_no.trim(),
            bank_details: formData.bank_details.trim(),
          }),
        }
      );

      const contentType = response.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok || !payload) {
        throw new Error(payload?.message || "Unable to submit RTGS application.");
      }

      setSuccessMessage(payload.message || "RTGS application submitted successfully.");
      resetForm(defaultBankDetails?.display_text || "");
      await fetchApplications();
    } catch (saveError) {
      console.error("Error submitting RTGS application:", saveError);
      setSuccessMessage("");
      setError(saveError.message || "Unable to submit RTGS application.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setError("");
    setSuccessMessage("");
    resetForm(defaultBankDetails?.display_text || "");
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card p-2">
            <div className="card-body">
              <p
                className="text-center fw-bold mb-3"
                style={{ fontSize: "18px" }}
              >
                RTGS SUBMIT APPLICATION
              </p>

              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    style={{ width: "150px" }}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ width: "150px" }}
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    style={{ width: "150px" }}
                    onClick={() => navigate("/student/dashboards")}
                  >
                    Close
                  </button>
                </div>
              </div>

              {error ? <div className="alert alert-danger">{error}</div> : null}
              {successMessage ? (
                <div className="alert alert-success">{successMessage}</div>
              ) : null}

              <div className="row mt-3 mx-2 mt-2">
                <div className="col-12 custom-section-box">
                  <Row className="justify-content-center">
                    <Col className="p-3" xs={12} sm={8}>
                      <Form>
                        <Row className="mb-1">
                          <Col md={6} className="py-0">
                            <Form.Label className="mb-1">
                              Remitter bank&apos;s Name & Branch{" "}
                            </Form.Label>
                          </Col>
                          <Col md={6} className="py-0">
                            <Form.Control
                              type="text"
                              className="form-control form-control-sm detail"
                              name="remitter_bank_name"
                              value={formData.remitter_bank_name}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>
                              Name of the Account Holder/Transferred From{" "}
                            </Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              className="form-control form-control-sm detail"
                              name="account_holder_name"
                              value={formData.account_holder_name}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>Contact No</Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              className="form-control detail"
                              name="contact_no"
                              value={formData.contact_no}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>
                              Transaction Date <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                          </Col>
                          <Col md={6}>
                            <DatePicker
                              selected={transactionDate}
                              onChange={setTransactionDate}
                              className="form-control detail"
                              dateFormat="yyyy-MM-dd"
                              placeholderText="Select transaction date"
                              maxDate={new Date()}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>Amount</Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              className="form-control detail"
                              name="amount"
                              value={formData.amount}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>
                              Amount in words <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              name="amount_in_words"
                              className="form-control detail"
                              value={formData.amount_in_words}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>IFSC Code </Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              name="ifsc_code"
                              className="form-control detail"
                              value={formData.ifsc_code}
                              onChange={handleChange}
                              maxLength={11}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>Settled UTR/Reference No </Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              name="utr_reference_no"
                              className="form-control detail"
                              value={formData.utr_reference_no}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>Reference No</Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              name="reference_no"
                              className="form-control detail"
                              value={formData.reference_no}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-1">
                          <Col md={6}>
                            <Form.Label>Bank Details</Form.Label>
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              type="text"
                              name="bank_details"
                              className="form-control detail"
                              value={formData.bank_details}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="col-12 mt-3">
                {loading ? (
                  <div className="text-center py-4">Loading application history...</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Sr.No</th>
                          <th>RTGS/NEFT Date</th>
                          <th>Bank Name</th>
                          <th>NEFT Code</th>
                          <th>UTR No</th>
                          <th>Ac Holder Name</th>
                          <th>SAP Bank GL AC No </th>
                          <th>Amount</th>
                          <th>BEC Bank Account No</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.length > 0 ? (
                          applications.map((item) => (
                            <tr key={item.id}>
                              <td>{item.sr_no}</td>
                              <td>{item.transaction_date || "-"}</td>
                              <td>{item.remitter_bank_name || "-"}</td>
                              <td>{item.ifsc_code || "-"}</td>
                              <td>{item.utr_reference_no || "-"}</td>
                              <td>{item.account_holder_name || "-"}</td>
                              <td>{item.reference_no || "-"}</td>
                              <td>{item.amount || "-"}</td>
                              <td>{item.bank_details || "-"}</td>
                              <td></td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center">
                              No RTGS applications submitted yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StdRTGS;
